import { initializeApp } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator, Firestore } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator, Functions } from 'firebase/functions';
import { getAuth, connectAuthEmulator, Auth } from 'firebase/auth';
import { getStorage, connectStorageEmulator, FirebaseStorage } from 'firebase/storage';

const ACTIVE_DB_KEY = 'activeDatabaseId';
const DEFAULT_DB = '(default)';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db: Firestore = getFirestore(app);
const functions: Functions = getFunctions(app, 'europe-southwest1'); // Madrid region
const auth: Auth = getAuth(app);
const storage: FirebaseStorage = getStorage(app);

// For local development with emulators
const isDevelopment = import.meta.env.VITE_ENV === 'development';

if (isDevelopment) {
  console.log('🔧 Running in development mode with emulators');

  // Connect to Firestore emulator
  connectFirestoreEmulator(db, 'localhost', 8080);

  // Connect to Functions emulator
  connectFunctionsEmulator(functions, 'localhost', 5001);

  // Connect to Auth emulator
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });

  // Connect to Storage emulator
  connectStorageEmulator(storage, 'localhost', 9199);
}

/**
 * Returns the Firestore instance for the currently active database.
 * Reads the database ID from localStorage (set by AuthContext).
 */
export function getDb(): Firestore {
  const databaseId = localStorage.getItem(ACTIVE_DB_KEY) || DEFAULT_DB;
  return getFirestore(app, databaseId);
}

/**
 * Returns the callable function name with the appropriate DB suffix.
 * Use this instead of hardcoding function names when calling Cloud Functions
 * so the call is routed to the correct per-database variant.
 *
 * @example httpsCallable(functions, getCallableName('setUserAdminStatus'))
 */
export function getCallableName(fnName: string): string {
  const databaseId = localStorage.getItem(ACTIVE_DB_KEY) || DEFAULT_DB;
  return databaseId === DEFAULT_DB ? fnName : `${databaseId}-${fnName}`;
}

export { ACTIVE_DB_KEY, DEFAULT_DB };
export { app, db, functions, auth, storage };
